import { AllowedMaterialSpec, isPortOfLoadingRequired, type SimpleTradeCompanyListItem, SiteMaterialList } from 'api';
import {
  AllowedMaterialSpecAutocomplete,
  DateField,
  IncotermSelectField,
  MaterialGradeAutocomplete,
  PortAutocomplete,
  PriceAsCurrencyOrIndex,
  SiteMaterialWithProductAutocomplete,
  TextField,
  TradeCompanyAutocomplete,
  TradeCompanyLeadAutocomplete,
  useFormContext,
  useWatch,
} from 'form';
import { useTranslation } from 'i18n';
import { useState } from 'react';
import { ActionRow, Button, Stack } from 'ui';

import { getCreateSiteMaterialUrl, NoSiteMaterialsOption } from '../../../site-materials';
import { CompanySourceEnum, type CompanySourceType } from '../../enums';
import { SupplySignalFormValues } from '../../schema/SupplySignalSchema/SupplySignalSchema';
import { CompanySourceToggleButton } from '../CompanySourceToggleButton/CompanySourceToggleButton';

export const SupplySignalFormSection = () => {
  const { t } = useTranslation('orders');
  const [selectedCompanyType, setSelectedCompanyType] = useState<CompanySourceType>(CompanySourceEnum.GROUND_CONTROL);
  const form = useFormContext<SupplySignalFormValues>();
  const { setValue } = form;
  const [incoterm, tradeCompanyId, siteMaterialId, portOfLoadingId] = useWatch({
    name: ['incoterm', 'trade_company_id', 'site_material_id', 'port_of_loading_id'],
  });
  const isGroundControlSelected = selectedCompanyType === CompanySourceEnum.GROUND_CONTROL;

  const onTradeCompanyChange = (tradeCompany: SimpleTradeCompanyListItem) => {
    setValue('site_material_id', undefined);
    if (tradeCompany.currency) {
      setValue('currency', tradeCompany.currency);
    }
    if (tradeCompany.preferred_port_of_loading_id) {
      setValue('port_of_loading_id', tradeCompany.preferred_port_of_loading_id);
    } else {
      setValue('port_of_loading_id', undefined);
    }
  };

  const onTradeCompanyTypeChange = (tradeCompanyType: CompanySourceType) => {
    setValue('trade_company_id', undefined);
    setValue('trade_company_lead_id', undefined);
    setValue('site_material_id', undefined);
    setValue('material_spec_id', undefined);
    setValue('material_grade_id', undefined);
    setSelectedCompanyType(tradeCompanyType);
  };

  const onMaterialSpecChange = (materialSpec: AllowedMaterialSpec) => {
    if (materialSpec?.default_payload_size) {
      setValue('tonnes_per_load', Number(materialSpec.default_payload_size));
    }
  };

  const onSiteMaterialChange = (siteMaterial: SiteMaterialList) => {
    if (siteMaterial?.default_payload_size) {
      setValue('tonnes_per_load', Number(siteMaterial.default_payload_size));
      return;
    }
    if (siteMaterial?.material_spec.default_payload_size) {
      setValue('tonnes_per_load', Number(siteMaterial.material_spec.default_payload_size));
    }
  };

  return (
    <>
      <CompanySourceToggleButton
        onTradeCompanyTypeChange={onTradeCompanyTypeChange}
        tradeCompanyType={selectedCompanyType}
      />
      {isGroundControlSelected ? (
        <>
          <TradeCompanyAutocomplete
            name="trade_company_id"
            label={t('tradeSignals.supplyForm.labels.selectSellerFromGroundControl')}
            onlySellers={true}
            onSelectOption={(tradeCompany) => onTradeCompanyChange(tradeCompany)}
          />
          <Stack
            direction="column"
            sx={{
              justifyContent: 'center',
              alignItems: 'flex-end',
              gap: 1,
            }}
          >
            <ActionRow>
              <Button
                variant="text"
                component="a"
                disabled={!siteMaterialId}
                target="_blank"
                href={getCreateSiteMaterialUrl(tradeCompanyId)}
                aria-label={t('tradeSignals.supplyForm.actions.addNewSiteMaterialAriaLabel')}
              >
                {t('tradeSignals.supplyForm.actions.addNewSiteMaterial')}
              </Button>
              <Button
                variant="text"
                component="a"
                disabled={!siteMaterialId}
                target="_blank"
                href={`/quality/${siteMaterialId}/`}
                aria-label={t('tradeSignals.supplyForm.actions.linkToQualityReportAriaLabel')}
              >
                {t('tradeSignals.supplyForm.actions.linkToQualityReport')}
              </Button>
            </ActionRow>
            <SiteMaterialWithProductAutocomplete
              name="site_material_id"
              label={t('tradeSignals.supplyForm.labels.siteMaterial')}
              disabled={!tradeCompanyId}
              tradeCompanyId={tradeCompanyId}
              noOptionsText={<NoSiteMaterialsOption tradeCompanyId={tradeCompanyId} />}
              onSelectOption={onSiteMaterialChange}
              fullWidth
            />
          </Stack>
        </>
      ) : (
        <>
          <TradeCompanyLeadAutocomplete
            name="trade_company_lead_id"
            label={t('tradeSignals.supplyForm.labels.selectSellerFromHubspot')}
          />
          <AllowedMaterialSpecAutocomplete
            label={t('tradeSignals.common.labels.material')}
            name="material_spec_id"
            onSelectOption={onMaterialSpecChange}
          />
          <MaterialGradeAutocomplete label={t('tradeSignals.common.labels.materialGrade')} name="material_grade_id" />
        </>
      )}
      <PriceAsCurrencyOrIndex />
      <IncotermSelectField fullWidth />
      {isPortOfLoadingRequired(incoterm) && (
        <PortAutocomplete
          name="port_of_loading_id"
          label={t('tradeSignals.supplyForm.labels.selectPortOfLoading')}
          defaultValue={portOfLoadingId}
        />
      )}
      <TextField
        label={t('tradeSignals.common.labels.tonnes')}
        name="tonnes"
        type="number"
        required
        data-testid="tonnes"
      />
      <TextField
        label={t('tradeSignals.supplyForm.labels.tonnesPerLoad')}
        name="tonnes_per_load"
        type="number"
        required
      />
      <DateField label={t('tradeSignals.supplyForm.labels.saleTargetDate')} name="sale_target_date" />
      <DateField label={t('tradeSignals.supplyForm.labels.lastCollectionDate')} name="last_collection_date" required />
      <TextField
        multiline
        label={t('tradeSignals.common.labels.standupNotes')}
        name="notes"
        aria-label={t('tradeSignals.common.labels.notes')}
      />
    </>
  );
};
